import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { resolvePrice } from '../utils/pricing';

const CartContext = createContext();

/**
 * Build a stable cart key for a catalog item.
 *
 * Includes:
 *   - productId
 *   - variantId
 *   - sorted selected options (so order doesn't matter)
 *
 * This ensures two different option combinations on the same variant
 * are always treated as separate cart items.
 */
function buildCatalogKey(productId, variantId, selectedOptions = []) {
  const optionStr = [...selectedOptions]
    .sort((a, b) => String(a.optionGroupId || a.optionGroupName).localeCompare(String(b.optionGroupId || b.optionGroupName)))
    .map(o => `${o.optionGroupId || o.optionGroupName}:${o.optionValue}`)
    .join('|');
  return `catalog-${productId}-${variantId}-${optionStr}`;
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('printspace_cart')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('printspace_cart', JSON.stringify(cart));
  }, [cart]);

  /**
   * addToCart supports two modes:
   *
   * Legacy (old Product model):
   *   addToCart(product, { size, color, quantity, price, sizePrice, pricingTiers })
   *
   * Catalog (new CatalogProduct + Variant):
   *   addToCart(product, {
   *     variantId, variantName, variantSku,
   *     selectedOptions,    // [{optionGroupId, optionGroupName, optionValue, ...}]
   *     quantity,
   *     unitPrice,          // total price for this qty from pricing tiers
   *     pricingTiers,       // [{quantity, price}] from variant
   *     minimumQuantity,
   *     quantityIncrement,
   *     variantImage,
   *     isCatalog: true,
   *   })
   */
  const addToCart = (product, options = {}) => {
    setCart(prev => {
      if (options.isCatalog) {
        // ── Catalog mode ───────────────────────────────────────────
        const key = buildCatalogKey(product.id, options.variantId, options.selectedOptions);
        const existing = prev.find(i => i.cartKey === key);

        if (existing) {
          toast.success('Quantity updated!');
          const newQty   = options.quantity;
          const newPrice = resolvePrice(options.pricingTiers || [], newQty) ?? options.unitPrice;
          return prev.map(i =>
            i.cartKey === key
              ? { ...i, quantity: newQty, unitPrice: newPrice, totalPrice: newPrice * newQty }
              : i
          );
        }

        toast.success('Added to cart!');
        return [...prev, {
          cartKey:           key,
          isCatalog:         true,
          id:                product.id,
          name:              product.name,
          sku:               product.sku || '',          // ← product SKU added
          slug:              product.slug,
          image:             options.variantImage || product.mainImage || '',
          categoryName:      product.category?.name || '',
          categorySlug:      product.category?.slug || '',
          subcategorySlug:   product.subcategory?.slug || '',
          variantId:         options.variantId   || null,
          variantName:       options.variantName  || '',
          variantSku:        options.variantSku   || '',
          selectedOptions:   options.selectedOptions || [],
          quantity:          options.quantity || 1,
          unitPrice:         options.unitPrice || 0,
          totalPrice:        (options.unitPrice || 0) * (options.quantity || 1),
          pricingTiers:      options.pricingTiers || [],
          minimumQuantity:   options.minimumQuantity   || 1,
          quantityIncrement: options.quantityIncrement || 1,
        }];

      } else {
        // ── Legacy mode ────────────────────────────────────────────
        const key      = `${product.id}-${options.size || ''}-${options.color || ''}`;
        const existing = prev.find(i => i.cartKey === key);
        if (existing) {
          toast.success('Quantity updated!');
          const newQty   = existing.quantity + (options.quantity || 1);
          return prev.map(i => {
            if (i.cartKey !== key) return i;
            const tiers = [...(i.pricingTiers || [])].filter(t => t.qty && t.price).sort((a, b) => a.qty - b.qty);
            let newPrice = i.sizePrice || i.price;
            if (tiers.length) {
              if (newQty <= tiers[0].qty) newPrice = parseFloat(tiers[0].price);
              else if (newQty >= tiers[tiers.length - 1].qty) newPrice = parseFloat(tiers[tiers.length - 1].price);
              else {
                for (let j = 0; j < tiers.length - 1; j++) {
                  if (newQty >= tiers[j].qty && newQty <= tiers[j + 1].qty) {
                    const ratio = (newQty - tiers[j].qty) / (tiers[j + 1].qty - tiers[j].qty);
                    newPrice = parseFloat((tiers[j].price - ratio * (tiers[j].price - tiers[j + 1].price)).toFixed(2));
                    break;
                  }
                }
              }
            }
            return { ...i, quantity: newQty, price: newPrice };
          });
        }
        toast.success('Added to cart!');
        return [...prev, {
          cartKey:      key,
          isCatalog:    false,
          id:           product.id,
          name:         product.name,
          sku:          product.sku || '',
          price:        options.price || parseFloat(product.salePrice || product.price),
          sizePrice:    options.sizePrice || parseFloat(product.salePrice || product.price),
          pricingTiers: options.pricingTiers || [],
          image:        product.mainImage,
          slug:         product.slug,
          quantity:     options.quantity || 1,
          size:         options.size || '',
          color:        options.color || '',
        }];
      }
    });
  };

  const removeFromCart = (cartKey) => {
    setCart(prev => prev.filter(i => i.cartKey !== cartKey));
    toast.success('Item removed.');
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity < 1) return removeFromCart(cartKey);
    setCart(prev => prev.map(i => {
      if (i.cartKey !== cartKey) return i;

      if (i.isCatalog) {
        const newPrice = resolvePrice(i.pricingTiers || [], quantity) ?? i.unitPrice;
        return { ...i, quantity, unitPrice: newPrice, totalPrice: newPrice * quantity };
      }

      // Legacy
      const tiers = [...(i.pricingTiers || [])].filter(t => t.qty && t.price).sort((a, b) => a.qty - b.qty);
      let newPrice = i.sizePrice || i.price;
      if (tiers.length) {
        if (quantity <= tiers[0].qty) newPrice = parseFloat(tiers[0].price);
        else if (quantity >= tiers[tiers.length - 1].qty) newPrice = parseFloat(tiers[tiers.length - 1].price);
        else {
          for (let j = 0; j < tiers.length - 1; j++) {
            if (quantity >= tiers[j].qty && quantity <= tiers[j + 1].qty) {
              const ratio = (quantity - tiers[j].qty) / (tiers[j + 1].qty - tiers[j].qty);
              newPrice = parseFloat((tiers[j].price - ratio * (tiers[j].price - tiers[j + 1].price)).toFixed(2));
              break;
            }
          }
        }
      }
      return { ...i, quantity, price: newPrice };
    }));
  };

  const clearCart = () => setCart([]);

  // Unified price per item (works for both modes)
  const getItemPrice = (item) => item.isCatalog ? (item.unitPrice || 0) : (item.price || 0);

  // For catalog items use stored totalPrice (unitPrice × quantity already computed on add/update)
  // For legacy items: price is per-piece so multiply by quantity
  const subtotal = cart.reduce((s, i) => {
    if (i.isCatalog) return s + (parseFloat(i.totalPrice) || (i.unitPrice || 0) * i.quantity);
    return s + (i.price || 0) * i.quantity;
  }, 0);

  // ── Cart count = number of unique cart items (not total quantity) ──
  const cartCount = cart.length;

  // Keep totalItems for any legacy usage but cartCount is the canonical badge count
  const totalItems = cartCount;

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      subtotal,
      totalItems,   // unique item count — used for navbar badge
      cartCount,    // alias
      getItemPrice,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);